import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

function DropdownLink({ to, children, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        color: hovered ? theme.colors.primary : theme.colors.text,
        textDecoration: 'none',
        fontSize: '0.9rem',
        display: 'block',
        padding: '9px 18px',
        whiteSpace: 'nowrap',
        backgroundColor: hovered ? `${theme.colors.primary}11` : 'transparent',
      }}
    >
      {children}
    </Link>
  );
}

function Navbar({ user, roleId, perUserCalendar, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  const handleLogout = () => {
    onLogout();
    closeAll();
    navigate('/');
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Al pasar de móvil a escritorio (o navegar) cerramos los menús para no dejar
  // paneles abiertos colgando.
  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [isMobile, location.pathname]);

  if (location.pathname.startsWith('/p/') || location.pathname.startsWith('/tienda/') || location.pathname.startsWith('/cita/') || location.pathname.startsWith('/ticket/') || location.pathname.startsWith('/news/')) return null;

  const toggle = (name) => setOpenMenu((prev) => (prev === name ? null : name));
  const close = () => setOpenMenu(null);
  const closeAll = () => { setOpenMenu(null); setMobileOpen(false); };

  const is123 = user && (roleId === 1 || roleId === 2 || roleId === 3);

  // Definición única de los menús desplegables. Tanto la vista de escritorio
  // (dropdowns) como la de móvil (acordeón) se construyen a partir de aquí.
  const sections = [
    {
      name: 'web',
      label: 'Mi página web',
      links: [
        { to: '/productos', label: 'Productos' },
        { to: '/servicios', label: 'Servicios' },
        { to: '/utilidades-noticias', label: 'Noticias' },
        { to: '/utilidades-tickets', label: 'Tickets' },
        { to: '/cajero', label: 'Cajero' },
        (roleId === 1 || roleId === 2) && { to: '/empresa', label: 'Empresa' },
      ].filter(Boolean),
    },
    {
      name: 'adminweb',
      label: 'Admin. página',
      links: [
        { to: '/pedidos', label: 'Pedidos' },
        { to: '/citas', label: 'Citas' },
        { to: '/mis-links', label: 'Ver links' },
        (roleId === 2 || roleId === 3) && { to: '/transacciones-clientes', label: 'Transacciones clientes' },
        { to: '/listas-contactos', label: 'Listas de contactos' },
      ].filter(Boolean),
    },
    {
      name: 'salida',
      label: 'Salida',
      links: [
        { to: '/plantillas', label: 'Plantillas' },
        { to: '/publicidad', label: 'Publicidad' },
      ],
    },
    {
      name: 'entrada',
      label: 'Entrada',
      links: [
        { to: '/conversaciones', label: 'Conversaciones' },
      ],
    },
    {
      name: 'config',
      label: 'Configuración',
      links: [
        roleId === 1 && { to: '/companies', label: 'Compañías' },
        (roleId === 1 || roleId === 2) && { to: '/users', label: 'Usuarios' },
        perUserCalendar && (roleId === 2 || roleId === 3) && { to: '/configuracion-horario', label: 'Configuración horario' },
        { to: '/channels', label: 'Canales' },
        { to: '/listas-contactos', label: 'Listas de contactos' },
        { to: '/flows', label: 'Flujos' },
        { to: '/campaigns', label: 'Campañas' },
        { to: '/tipificaciones', label: 'Tipificaciones' },
        (roleId === 1 || roleId === 2) && { to: '/cajero-config', label: 'Cajero' },
        (roleId === 1 || roleId === 2) && { to: '/empresa', label: 'Empresa' },
      ].filter(Boolean),
    },
    {
      name: 'util',
      label: 'Utilidades',
      links: [
        { to: '/campos-personalizados', label: 'Campos personalizados' },
        { to: '/plantillas-email', label: 'Plantillas de correo' },
        { to: '/plantillas-pdf', label: 'Plantillas para PDF' },
        { to: '/formularios', label: 'Formularios' },
        { to: '/archivos', label: 'Archivo' },
        { to: '/tutoriales', label: 'Tutoriales' },
        { to: '/tarifas', label: 'Tarifas' },
      ].filter(Boolean),
    },
  ];

  const sectionBtn = (name, label) => (
    <button
      onClick={() => toggle(name)}
      style={{
        background: 'none',
        border: 'none',
        color: openMenu === name ? theme.colors.primary : theme.colors.text,
        cursor: 'pointer',
        fontSize: '0.95rem',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        padding: 0,
        fontFamily: 'inherit',
      }}
    >
      {label}
      <span style={{
        fontSize: '0.55rem',
        transform: openMenu === name ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.15s',
        display: 'inline-block',
        marginTop: openMenu === name ? '1px' : '-1px',
      }}>▼</span>
    </button>
  );

  const dropdown = (children) => (
    <div style={{
      position: 'absolute',
      top: 'calc(100% + 10px)',
      left: 0,
      backgroundColor: theme.colors.surface,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: '10px',
      boxShadow: `0 8px 32px rgba(0,0,0,0.4)`,
      minWidth: '170px',
      zIndex: 200,
      overflow: 'hidden',
    }}>
      {children}
    </div>
  );

  const navBarStyle = {
    backgroundColor: theme.colors.background,
    borderBottom: `1px solid ${theme.colors.primary}`,
    padding: isMobile ? '0 1rem' : '0 2rem',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: `0 2px 12px ${theme.colors.glowStrong}`,
  };

  const logo = (
    <Link to="/" onClick={closeAll} style={{ color: theme.colors.primary, textDecoration: 'none', fontSize: isMobile ? '1.05rem' : '1.25rem', fontWeight: '700', letterSpacing: '0.5px' }}>
      New horizont start
    </Link>
  );

  // ─────────────────────────── MÓVIL ───────────────────────────
  if (isMobile) {
    const mobileLinkStyle = {
      color: theme.colors.text,
      textDecoration: 'none',
      fontSize: '1rem',
      display: 'block',
      padding: '14px 4px',
      borderBottom: `1px solid ${theme.colors.border}33`,
    };
    const mobileSubLinkStyle = {
      ...mobileLinkStyle,
      fontSize: '0.92rem',
      padding: '11px 4px 11px 22px',
      color: theme.colors.textMuted,
    };

    return (
      <nav ref={navRef} style={navBarStyle}>
        {logo}

        <button
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Menú"
          style={{
            background: 'none',
            border: 'none',
            color: theme.colors.primary,
            cursor: 'pointer',
            fontSize: '1.6rem',
            lineHeight: 1,
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {mobileOpen ? '✕' : '☰'}
        </button>

        {mobileOpen && (
          <div style={{
            position: 'absolute',
            top: '64px',
            left: 0,
            right: 0,
            backgroundColor: theme.colors.background,
            borderBottom: `1px solid ${theme.colors.primary}`,
            boxShadow: `0 8px 24px rgba(0,0,0,0.5)`,
            maxHeight: 'calc(100vh - 64px)',
            overflowY: 'auto',
            padding: '0.5rem 1.25rem 1.25rem',
            zIndex: 99,
          }}>
            {user && (
              <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem', padding: '12px 4px', margin: 0, borderBottom: `1px solid ${theme.colors.border}33` }}>
                Bienvenido, <strong style={{ color: theme.colors.primary }}>{user}</strong>
              </p>
            )}

            <Link to="/" onClick={closeAll} style={mobileLinkStyle}>Home</Link>

            {!user && (
              <>
                <Link to="/vitrina-productos" onClick={closeAll} style={mobileLinkStyle}>Vitrina de productos</Link>
                <Link to="/vitrina-servicios" onClick={closeAll} style={mobileLinkStyle}>Vitrina de servicios</Link>
                <Link to="/tutoriales" onClick={closeAll} style={mobileLinkStyle}>Tutoriales</Link>
                <Link to="/tarifas" onClick={closeAll} style={mobileLinkStyle}>Tarifas</Link>
                <Link to="/terminos" onClick={closeAll} style={mobileLinkStyle}>Términos y condiciones</Link>
              </>
            )}

            {is123 && (
              <Link to="/recargar" onClick={closeAll} style={mobileLinkStyle}>Recargar</Link>
            )}

            {is123 && sections.map((sec) => (
              <div key={sec.name}>
                <button
                  onClick={() => toggle(sec.name)}
                  style={{
                    ...mobileLinkStyle,
                    width: '100%',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    borderBottom: `1px solid ${theme.colors.border}33`,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: openMenu === sec.name ? theme.colors.primary : theme.colors.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {sec.label}
                  <span style={{
                    fontSize: '0.6rem',
                    transform: openMenu === sec.name ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                    display: 'inline-block',
                  }}>▼</span>
                </button>
                {openMenu === sec.name && sec.links.map((l) => (
                  <Link key={l.to} to={l.to} onClick={closeAll} style={mobileSubLinkStyle}>
                    {l.label}
                  </Link>
                ))}
              </div>
            ))}

            {user ? (
              <button
                onClick={handleLogout}
                style={{
                  marginTop: '1rem',
                  width: '100%',
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.colors.primary}`,
                  color: theme.colors.primary,
                  padding: '11px 18px',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontFamily: 'inherit',
                }}
              >
                Cerrar sesión
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <Link to="/login" onClick={closeAll} style={{ flex: 1, textAlign: 'center', color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem', padding: '11px', border: `1px solid ${theme.colors.border}`, borderRadius: '7px' }}>Login</Link>
                <Link to="/register" onClick={closeAll} style={{ flex: 1, textAlign: 'center', color: theme.colors.background, backgroundColor: theme.colors.primary, textDecoration: 'none', fontSize: '0.95rem', padding: '11px', borderRadius: '7px', fontWeight: 700 }}>Registrarse</Link>
              </div>
            )}
          </div>
        )}
      </nav>
    );
  }

  // ─────────────────────────── ESCRITORIO ───────────────────────────
  return (
    <nav ref={navRef} style={navBarStyle}>
      {logo}

      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>

        <Link to="/" onClick={close} style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>
          Home
        </Link>
        {!user && (
          <>
            <Link to="/vitrina-productos" onClick={close} style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>
              Vitrina de productos
            </Link>
            <Link to="/vitrina-servicios" onClick={close} style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>
              Vitrina de servicios
            </Link>
            <Link to="/tutoriales" onClick={close} style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>
              Tutoriales
            </Link>
            <Link to="/tarifas" onClick={close} style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>
              Tarifas
            </Link>
            <Link to="/terminos" onClick={close} style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>
              Términos y condiciones
            </Link>
          </>
        )}
        {is123 && (
        <Link to="/recargar" onClick={close} style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>
        Recargar
        </Link>
        )}

        {is123 && sections.map((sec) => (
          <div key={sec.name} style={{ position: 'relative' }}>
            {sectionBtn(sec.name, sec.label)}
            {openMenu === sec.name && dropdown(
              sec.links.map((l) => (
                <DropdownLink key={l.to} to={l.to} onClick={close}>{l.label}</DropdownLink>
              ))
            )}
          </div>
        ))}

        {user ? (
          <>
            <span style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>
              Bienvenido, <strong style={{ color: theme.colors.primary }}>{user}</strong>
            </span>
            <button
              onClick={handleLogout}
              style={{
                backgroundColor: 'transparent',
                border: `1px solid ${theme.colors.primary}`,
                color: theme.colors.primary,
                padding: '6px 18px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              Cerrar sesión
            </button>
          </>
        ) : (
          <>
            <Link to="/login" style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>Login</Link>
            <Link to="/register" style={{ color: theme.colors.text, textDecoration: 'none', fontSize: '0.95rem' }}>Registrarse</Link>
          </>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
